import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { AutoComplete, Button, Input, Select, Space, Typography, Empty } from "antd";
import { PlusOutlined, DeleteOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { Checkbox, Switch, Divider, Segmented, InputNumber, DatePicker, Tag } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getUserRoleSubscriptionWiseListApi, getUserApi } from "../../../apiservice/setting";
import { getFieldOptions, listCustomActions } from "../../../apiservice/workflow";
import FieldPathPicker, { fieldTypeForPath, fieldDefForPath } from "../../../components/FieldPathPicker";
import "./graphEditor.css";

const { Text } = Typography;

const ACTION_TYPES = [
  "Send Email",
  "Send Notification",
  "Assign to User",
  "Assign to Role",
  "Update Field",
  "Assign Task",
];
const OPERATORS = ["equals", "not_equals", ">", "<", ">=", "<=", "contains", "in", "between"];

// Modules with a declared accounts relation in backend/src/workflow/relations.js
// — the only ones where an Aggregate condition's "same vendor/customer" match
// is well-defined. Mirrors that file; update both together if it changes.
const AGGREGATE_SOURCE_MODULES = ["purchase_order", "purchase", "sales", "quotation", "sales_return", "service"];

const DEFAULT_GRAPH = {
  nodes: [
    { id: "n_start", type: "start", position: { x: 240, y: 30 }, data: { label: "Start" } },
    { id: "n_approve", type: "action", position: { x: 220, y: 150 }, data: { label: "Approve", actionType: "Assign to User", dueInHours: 48 } },
  ],
  edges: [{ id: "e1", source: "n_start", target: "n_approve" }],
};

/* ── Custom nodes ─────────────────────────────────────────────────────── */
function StartNode({ data }) {
  return (
    <div className="wf-node wf-node-start">
      <div className="wf-node-title">▶ {data.label || "Start"}</div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}
function conditionSummary(conditions) {
  const rows = conditions && conditions.type === "group" ? conditions.conditions : conditions;
  const list = Array.isArray(rows) ? rows.filter((c) => c && c.field) : [];
  if (list.length === 0) return "(set a condition)";
  const joiner = conditions && conditions.type === "group" ? conditions.joiner || "AND" : "AND";
  const parts = list.map((c) => `${c.field} ${c.operator || ""} ${c.value ?? ""}`.trim());
  return list.length > 1 ? parts.join(` ${joiner} `) : parts[0];
}
function ConditionNode({ data, selected }) {
  return (
    <div className={"wf-node wf-node-condition" + (selected ? " selected" : "")}>
      <Handle type="target" position={Position.Top} />
      <div className="wf-node-title">◇ {data.label || "Condition"}</div>
      <div className="wf-node-sub">{conditionSummary(data.conditions)}</div>
      <Handle type="source" position={Position.Bottom} id="yes" style={{ left: "28%" }} />
      <Handle type="source" position={Position.Bottom} id="no" style={{ left: "72%" }} />
      <span className="wf-handle-label yes">Yes</span>
      <span className="wf-handle-label no">No</span>
    </div>
  );
}
function ActionNode({ data, selected }) {
  return (
    <div className={"wf-node wf-node-action" + (selected ? " selected" : "")}>
      <Handle type="target" position={Position.Top} />
      <div className="wf-node-title">⚙ {data.label || data.actionType || "Action"}</div>
      <div className="wf-node-sub">{data.actionType || "(pick an action)"}</div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}
const nodeTypes = { start: StartNode, condition: ConditionNode, action: ActionNode };

/* ── Condition group helpers (v1: flat list, AND/OR at the group level) ── */
const EMPTY_CONDITION = { field: "", operator: "equals", valueType: "static", value: "" };

// Aggregate condition (narrow v1 scope: SUM a numeric field across sibling
// rows of one module — the anchor by default, or another module sharing the
// same accounts relation (see AGGREGATE_SOURCE_MODULES) — within the
// current calendar month. See backend/src/workflow/actions.js's
// evaluateAggregateCondition. "Same vendor/customer" is derived automatically
// from each module's own relations.js entry, not stored here — there's no
// matchField. dateField is fixed to sort_date (a real DB timestamp, not the
// free-text created_date column) and not user-editable.
function makeEmptyAggregateCondition(anchorModuleKey) {
  return {
    type: "aggregate",
    sourceModule: anchorModuleKey,
    aggregateField: "",
    dateField: "sort_date",
    period: "current_month",
    operator: "<=",
    valueType: "static",
    value: "",
  };
}

// Normalises node.data.conditions (old bare-array format, or already the
// new {type:"group",...} shape) into the new shape for editing. Old items'
// `field` was a flat string (e.g. "total_amount") with no module prefix;
// prefixing it with the workflow's own module turns it into a valid v1
// dot-path so the field picker can display it. This only affects local
// editor state — the record on disk isn't touched until Save.
function toConditionGroup(conditions, anchorModuleKey) {
  if (conditions && conditions.type === "group" && Array.isArray(conditions.conditions)) {
    return conditions;
  }
  if (Array.isArray(conditions) && conditions.length > 0) {
    return {
      type: "group",
      joiner: conditions.some((c) => c.logic === "OR") ? "OR" : "AND",
      conditions: conditions.map((c) => ({
        field: c.field && anchorModuleKey ? `${anchorModuleKey}.${c.field}` : c.field || "",
        operator: c.operator || "equals",
        valueType: "static",
        value: c.value ?? "",
      })),
    };
  }
  return { type: "group", joiner: "AND", conditions: [{ ...EMPTY_CONDITION }] };
}

function TypedValueInput({ type, value, onChange, lookupOptions, userOptions, placeholder }) {
  if (type === "lookup" || type === "user") {
    return (
      <Select
        style={{ width: "100%" }}
        showSearch
        allowClear
        optionFilterProp="label"
        placeholder={type === "user" ? "Select a user" : "Select a value"}
        value={value === "" || value == null ? undefined : value}
        onChange={(v) => onChange(v ?? "")}
        options={(type === "user" ? userOptions : lookupOptions) || []}
      />
    );
  }
  if (type === "number") {
    return (
      <InputNumber
        style={{ width: "100%" }}
        value={value === "" || value == null ? undefined : Number(value)}
        onChange={(v) => onChange(v ?? "")}
      />
    );
  }
  if (type === "date") {
    return (
      <DatePicker
        style={{ width: "100%" }}
        value={value ? dayjs(value) : null}
        onChange={(d) => onChange(d ? d.format("YYYY-MM-DD") : "")}
      />
    );
  }
  if (type === "boolean") {
    return (
      <Select
        style={{ width: "100%" }}
        value={value === "" || value == null ? undefined : String(value)}
        onChange={onChange}
        allowClear
        options={[{ value: "true", label: "True" }, { value: "false", label: "False" }]}
      />
    );
  }
  return <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || "e.g. 10000"} />;
}

/* ── Property panel ───────────────────────────────────────────────────── */
function PropertyPanel({
  node, onChange, onDelete, fieldOptions, userOptions, roleOptions, customActions = [],
  anchorModule, relatedModules = [], fieldsByModule = {}, modules = [], lookups = {},
}) {
  const actionTypeOptions = [
    ...ACTION_TYPES.map((a) => ({ value: a, label: a })),
    ...customActions.map((c) => ({ value: `Custom:${c.name}`, label: `Custom: ${c.label}` })),
  ];
  const onRejectOptions = [
    { value: "", label: "(none)" },
    ...customActions.map((c) => ({ value: c.name, label: c.label })),
  ];
  // Computed unconditionally (React hooks can't follow the early `!node`
  // return below) — d/conditionGroup default to an empty shape when there's
  // no node selected, which the effect below already guards against.
  const d = node?.data || {};
  const conditionGroup = toConditionGroup(d.conditions, anchorModule?.key);

  // Lazily fetches each distinct source module an Aggregate row currently
  // points at (other than the anchor module, whose fields already arrive
  // via fieldsByModule) — same lazy-fetch-and-cache idea as FieldPathPicker's
  // own loadData, just keyed by module instead of by Cascader node.
  const [sourceModuleFields, setSourceModuleFields] = useState({});
  const neededSourceModules = conditionGroup.conditions
    .filter((c) => c.type === "aggregate" && c.sourceModule && c.sourceModule !== anchorModule?.key)
    .map((c) => c.sourceModule);
  const neededKey = JSON.stringify([...new Set(neededSourceModules)]);
  useEffect(() => {
    const missing = [...new Set(neededSourceModules)].filter((m) => !sourceModuleFields[m]);
    missing.forEach((moduleKey) => {
      getFieldOptions(moduleKey)
        .then((res) => {
          const data = res?.data?.data || res?.data || {};
          setSourceModuleFields((prev) => ({ ...prev, [moduleKey]: data.fields || [] }));
        })
        .catch(() => setSourceModuleFields((prev) => ({ ...prev, [moduleKey]: [] })));
    });
  }, [neededKey]);

  // Raw JSON editor for a Custom: action node's own extra parameters (e.g.
  // finalizeLeaveRequest's targetStatus) — generic since each custom action
  // can need arbitrary fields no fixed form could anticipate. Reset whenever
  // the selected node changes; local text state so mid-edit invalid JSON
  // doesn't get committed or fight the textarea's cursor.
  const [customJson, setCustomJson] = useState("");
  const [customJsonError, setCustomJsonError] = useState(null);
  useEffect(() => {
    if (node?.type === "action" && typeof node?.data?.actionType === "string" && node.data.actionType.startsWith("Custom:")) {
      const { label, actionType, ...rest } = node.data || {};
      setCustomJson(JSON.stringify(rest, null, 2));
      setCustomJsonError(null);
    }
  }, [node?.id]);

  if (!node) {
    return (
      <div className="wf-graph-panel">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a node to edit it" />
        <div className="wf-panel-hint" style={{ marginTop: 12 }}>
          Drag from a node's bottom dot to another node's top to connect them.
          Condition nodes have <b>Yes</b> / <b>No</b> outputs. Select a node or edge and press
          Delete to remove it.
        </div>
        <div className="wf-panel-hint" style={{ marginTop: 8 }}>
          Info: Approval and Assign Task steps show up in <b>My approvals</b> and the right-sidebar <b>Workflow Tasks</b> list.
          Field names are matched leniently (for example <b>subtotal</b> and <b>sub_total</b>).
        </div>
      </div>
    );
  }
  const set = (patch) => onChange(patch);
  const fieldsForModule = (moduleKey) =>
    (moduleKey === anchorModule?.key ? fieldsByModule[anchorModule.key] : sourceModuleFields[moduleKey]) || [];
  const updateConditionRow = (idx, patch) => {
    const conditions = conditionGroup.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    set({ conditions: { ...conditionGroup, conditions } });
  };
  const addConditionRow = () => {
    set({ conditions: { ...conditionGroup, conditions: [...conditionGroup.conditions, { ...EMPTY_CONDITION }] } });
  };
  const removeConditionRow = (idx) => {
    const conditions = conditionGroup.conditions.filter((_, i) => i !== idx);
    set({ conditions: { ...conditionGroup, conditions: conditions.length ? conditions : [{ ...EMPTY_CONDITION }] } });
  };
  const setJoiner = (joiner) => set({ conditions: { ...conditionGroup, joiner } });

  return (
    <div className="wf-graph-panel">
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 10 }}>
        <Text strong style={{ textTransform: "capitalize" }}>{node.type} node</Text>
        {node.type !== "start" && (
          <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete}>Delete</Button>
        )}
      </Space>

      <Field label="Label">
        <Input value={d.label || ""} onChange={(e) => set({ label: e.target.value })} placeholder="Step name" />
      </Field>

      {node.type === "condition" && (
        <>
          {conditionGroup.conditions.map((cond, idx) => (
            <div key={idx} className="wf-condition-row">
              <div style={{ marginBottom: 8 }}>
                {idx > 0 && <Tag color="blue">{conditionGroup.joiner}</Tag>}
                <Tag color="cyan">Condition {idx + 1}</Tag>
              </div>
              <Field label="Type">
                <Segmented
                  size="small"
                  value={cond.type === "aggregate" ? "aggregate" : "simple"}
                  onChange={(v) => {
                    const conditions = conditionGroup.conditions.map((c, i) => (i === idx
                      ? (v === "aggregate" ? makeEmptyAggregateCondition(anchorModule?.key) : { ...EMPTY_CONDITION })
                      : c));
                    onChange({ conditions: { ...conditionGroup, conditions } });
                  }}
                  options={[{ label: "Simple", value: "simple" }, { label: "Aggregate", value: "aggregate" }]}
                  style={{ marginBottom: 6 }}
                />
              </Field>
              {cond.type === "aggregate" ? (
                <>
                  <Field label="Sum from module">
                    <Select
                      style={{ width: "100%" }}
                      value={cond.sourceModule || anchorModule?.key}
                      onChange={(v) => updateConditionRow(idx, { sourceModule: v, aggregateField: "" })}
                      options={AGGREGATE_SOURCE_MODULES.map((key) => ({
                        value: key,
                        label: modules.find((m) => m.key === key)?.label || key,
                      }))}
                    />
                  </Field>
                  <Field label="Sum this field">
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select a number field"
                      value={cond.aggregateField || undefined}
                      onChange={(v) => updateConditionRow(idx, { aggregateField: v })}
                      options={fieldsForModule(cond.sourceModule || anchorModule?.key)
                        .filter((f) => f.type === "number")
                        .map((f) => ({ value: f.name, label: f.label || f.name }))}
                    />
                  </Field>
                  <div className="wf-panel-hint" style={{ marginTop: -4, marginBottom: 8 }}>
                    Summed for the same vendor/customer as this {anchorModule?.label || "record"} — matched automatically, not configurable — created <b>this calendar month</b>.
                  </div>
                  <Field label="Operator">
                    <Select
                      style={{ width: "100%" }}
                      value={cond.operator || "<="}
                      onChange={(v) => updateConditionRow(idx, { operator: v })}
                      options={OPERATORS.map((o) => ({ value: o, label: o }))}
                    />
                  </Field>
                  <Field label="Value">
                    <Segmented
                      size="small"
                      value={cond.valueType || "static"}
                      onChange={(v) => updateConditionRow(idx, { valueType: v, value: "" })}
                      options={[{ label: "Value", value: "static" }, { label: "Field", value: "field" }]}
                      style={{ marginBottom: 6 }}
                    />
                    {cond.valueType === "field" ? (
                      <FieldPathPicker
                        value={cond.value}
                        onChange={(v) => updateConditionRow(idx, { value: v })}
                        anchorModule={anchorModule}
                        relatedModules={relatedModules}
                        fieldsByModule={fieldsByModule}
                      />
                    ) : (
                      <TypedValueInput type="number" value={cond.value} onChange={(v) => updateConditionRow(idx, { value: v })} />
                    )}
                  </Field>
                </>
              ) : (
                <>
                  <Field label="Field">
                    <FieldPathPicker
                      value={cond.field}
                      onChange={(v) => updateConditionRow(idx, { field: v })}
                      anchorModule={anchorModule}
                      relatedModules={relatedModules}
                      fieldsByModule={fieldsByModule}
                    />
                  </Field>
                  <Field label="Operator">
                    <Select
                      style={{ width: "100%" }}
                      value={cond.operator || "equals"}
                      onChange={(v) => updateConditionRow(idx, { operator: v })}
                      options={OPERATORS.map((o) => ({ value: o, label: o }))}
                    />
                  </Field>
                  <Field label="Value">
                    <Segmented
                      size="small"
                      value={cond.valueType || "static"}
                      onChange={(v) => updateConditionRow(idx, { valueType: v, value: "" })}
                      options={[{ label: "Value", value: "static" }, { label: "Field", value: "field" }]}
                      style={{ marginBottom: 6 }}
                    />
                    {cond.valueType === "field" ? (
                      <FieldPathPicker
                        value={cond.value}
                        onChange={(v) => updateConditionRow(idx, { value: v })}
                        anchorModule={anchorModule}
                        relatedModules={relatedModules}
                        fieldsByModule={fieldsByModule}
                      />
                    ) : (
                      <TypedValueInput
                        type={fieldTypeForPath(fieldsByModule, cond.field)}
                        lookupOptions={lookups[fieldDefForPath(fieldsByModule, cond.field)?.name]}
                        userOptions={userOptions}
                        value={cond.value}
                        onChange={(v) => updateConditionRow(idx, { value: v })}
                      />
                    )}
                  </Field>
                </>
              )}
              {conditionGroup.conditions.length > 1 && (
                <Button size="small" danger type="text" icon={<MinusCircleOutlined />} onClick={() => removeConditionRow(idx)}>
                  Remove condition
                </Button>
              )}
              {idx < conditionGroup.conditions.length - 1 && <Divider style={{ margin: "10px 0" }} />}
            </div>
          ))}
          <Button size="small" icon={<PlusOutlined />} onClick={addConditionRow} style={{ marginBottom: 10 }}>
            Add condition
          </Button>
          {conditionGroup.conditions.length > 1 && (
            <Field label="Match">
              <Select
                style={{ width: "100%" }}
                value={conditionGroup.joiner}
                onChange={setJoiner}
                options={[
                  { value: "AND", label: "ALL conditions (AND)" },
                  { value: "OR", label: "ANY condition (OR)" },
                ]}
              />
            </Field>
          )}
          <div className="wf-panel-hint">Connect the <b>Yes</b> output for a match, <b>No</b> otherwise.</div>
        </>
      )}

      {node.type === "action" && (
        <>
          <Field label="Action type">
            <Select
              style={{ width: "100%" }}
              value={d.actionType}
              placeholder="Pick an action"
              onChange={(v) => set({ actionType: v })}
              options={(() => {
                if (d.actionType && !actionTypeOptions.some((o) => o.value === d.actionType)) {
                  return [{ value: d.actionType, label: d.actionType }, ...actionTypeOptions];
                }
                return actionTypeOptions;
              })()}
            />
          </Field>
          {d.actionType === "Send Email" && (
            <>
              <Field label="To"><Input value={d.to || ""} onChange={(e) => set({ to: e.target.value })} placeholder="{{customer_email}}" /></Field>
              <Field label="Subject"><Input value={d.subject || ""} onChange={(e) => set({ subject: e.target.value })} /></Field>
              <Field label="Body"><Input.TextArea rows={3} value={d.body || ""} onChange={(e) => set({ body: e.target.value })} /></Field>
            </>
          )}
          {d.actionType === "Send Notification" && (
            <>
              <Field label="To user id"><Input value={d.to_user_id || ""} onChange={(e) => set({ to_user_id: e.target.value })} placeholder="user UUID" /></Field>
              <Field label="Title"><Input value={d.title || ""} onChange={(e) => set({ title: e.target.value })} /></Field>
              <Field label="Body"><Input.TextArea rows={2} value={d.body || ""} onChange={(e) => set({ body: e.target.value })} /></Field>
            </>
          )}
          {(d.actionType === "Assign to User" || d.actionType === "Assign to Role") && (
            <>
              <Field label={d.actionType === "Assign to User" ? "User" : "Role"}>
                <Select
                  style={{ width: "100%" }}
                  showSearch
                  optionFilterProp="label"
                  value={d.target || undefined}
                  placeholder={d.actionType === "Assign to User" ? "Select user" : "Select role"}
                  onChange={(v) => set({ target: v })}
                  options={(() => {
                    const options = d.actionType === "Assign to User" ? userOptions : roleOptions;
                    if (d.target && !options.some((o) => String(o.value) === String(d.target))) {
                      return [{ value: d.target, label: String(d.target) }, ...options];
                    }
                    return options;
                  })()}
                  allowClear
                />
              </Field>
              <Field label="Due in hours"><Input type="number" value={d.dueInHours ?? ""} onChange={(e) => set({ dueInHours: e.target.value ? Number(e.target.value) : undefined })} placeholder="48" /></Field>
              <div className="wf-panel-hint">This step pauses the workflow until the assignee approves/rejects.</div>
              <Field label="On reject, run">
                <Select
                  style={{ width: "100%" }}
                  value={d.onRejectHandler || ""}
                  onChange={(v) => set({ onRejectHandler: v || undefined })}
                  options={onRejectOptions}
                />
              </Field>
              <div className="wf-panel-hint">Optional — runs this custom action if this step is rejected, before the workflow stops.</div>
            </>
          )}
          {d.actionType === "Update Field" && (() => {
            // Resolve the picked field's own metadata (from the anchor
            // module's real field list, same source as the Condition
            // Node) so a "lookup"-typed target field (e.g. crm_lead's
            // source_id/purpose_id/status_id) gets the same dropdown-of-
            // real-values Value input as conditions do, instead of a
            // plain text box asking for a raw master-table UUID.
            const updateFieldDef = fieldsForModule(anchorModule?.key).find((f) => f.name === d.updateField);
            return (
              <>
                <Field label="Field to set">
                  <AutoComplete
                    style={{ width: "100%" }}
                    value={d.updateField || ""}
                    placeholder={fieldOptions.length > 0 ? "Select or type a field" : "e.g. status"}
                    onChange={(v) => set({ updateField: v, updateValue: "" })}
                    options={fieldOptions.map((field) => ({ value: field }))}
                    filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                    allowClear
                  />
                </Field>
                <Field label="Value">
                  <TypedValueInput
                    type={updateFieldDef?.type || "text"}
                    lookupOptions={lookups[d.updateField]}
                    userOptions={userOptions}
                    value={d.updateValue ?? ""}
                    onChange={(v) => set({ updateValue: v })}
                    placeholder="e.g. approved"
                  />
                </Field>
              </>
            );
          })()}
          {d.actionType === "Assign Task" && (
            <>
              <Field label="Assign to">
                <Select
                  style={{ width: "100%" }}
                  value={d.assignType || "user"}
                  onChange={(v) => set({ assignType: v, target: undefined })}
                  options={[{ value: "user", label: "User" }, { value: "role", label: "Role" }]}
                />
              </Field>
              <Field label={d.assignType === "role" ? "Role" : "User"}>
                <Select
                  style={{ width: "100%" }}
                  showSearch
                  optionFilterProp="label"
                  value={d.target || undefined}
                  placeholder={d.assignType === "role" ? "Select role" : "Select user"}
                  onChange={(v) => set({ target: v })}
                  options={(() => {
                    const opts = d.assignType === "role" ? roleOptions : userOptions;
                    if (d.target && !opts.some((o) => String(o.value) === String(d.target))) {
                      return [{ value: d.target, label: String(d.target) }, ...opts];
                    }
                    return opts;
                  })()}
                  allowClear
                />
              </Field>
              <Field label="Task title">
                <Input
                  value={d.taskTitle || ""}
                  onChange={(e) => set({ taskTitle: e.target.value })}
                  placeholder="e.g. Review documents"
                />
              </Field>
              <Field label="Description">
                <Input.TextArea
                  rows={2}
                  value={d.taskDescription || ""}
                  onChange={(e) => set({ taskDescription: e.target.value })}
                  placeholder="Instructions for the assignee"
                />
              </Field>
              <Divider orientation="left" plain style={{ fontSize: 12, margin: "8px 0" }}>Checklist</Divider>
              {(d.checklist || []).map((item, idx) => (
                <div key={item.id || idx} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                  <Input
                    size="small"
                    style={{ flex: 1 }}
                    value={item.label}
                    placeholder={`Step ${idx + 1}`}
                    onChange={(e) => {
                      const updated = (d.checklist || []).map((c, i) => i === idx ? { ...c, label: e.target.value } : c);
                      set({ checklist: updated });
                    }}
                  />
                  <Checkbox
                    checked={item.required}
                    onChange={(e) => {
                      const updated = (d.checklist || []).map((c, i) => i === idx ? { ...c, required: e.target.checked } : c);
                      set({ checklist: updated });
                    }}
                  >
                    <span style={{ fontSize: 11 }}>Required</span>
                  </Checkbox>
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => {
                      const updated = (d.checklist || []).filter((_, i) => i !== idx);
                      set({ checklist: updated });
                    }}
                  />
                </div>
              ))}
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  const id = `c_${Date.now()}`;
                  set({ checklist: [...(d.checklist || []), { id, label: "", required: false }] });
                }}
                style={{ marginBottom: 8 }}
              >
                Add checklist item
              </Button>
              <Divider orientation="left" plain style={{ fontSize: 12, margin: "8px 0" }}>File upload</Divider>
              <Field label="Allow file upload">
                <Switch
                  size="small"
                  checked={Boolean(d.allowFileUpload)}
                  onChange={(v) => set({ allowFileUpload: v, fileUploadRequired: v ? d.fileUploadRequired : false })}
                />
              </Field>
              {d.allowFileUpload && (
                <Field label="File upload required">
                  <Switch
                    size="small"
                    checked={Boolean(d.fileUploadRequired)}
                    onChange={(v) => set({ fileUploadRequired: v })}
                  />
                </Field>
              )}
              <Field label="Due in hours">
                <Input
                  type="number"
                  value={d.dueInHours ?? ""}
                  onChange={(e) => set({ dueInHours: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="48"
                />
              </Field>
              <div className="wf-panel-hint">
                Workflow pauses until the assignee completes all required checklist items
                {d.fileUploadRequired ? " and uploads a file" : ""}.
              </div>
              <Field label="On reject, run">
                <Select
                  style={{ width: "100%" }}
                  value={d.onRejectHandler || ""}
                  onChange={(v) => set({ onRejectHandler: v || undefined })}
                  options={onRejectOptions}
                />
              </Field>
              <div className="wf-panel-hint">Optional — runs this custom action if this task is rejected.</div>
            </>
          )}
          {typeof d.actionType === "string" && d.actionType.startsWith("Custom:") && (
            <>
              <Field label="Custom action settings (JSON)">
                <Input.TextArea
                  rows={4}
                  value={customJson}
                  onChange={(e) => {
                    const text = e.target.value;
                    setCustomJson(text);
                    try {
                      const parsed = JSON.parse(text || "{}");
                      setCustomJsonError(null);
                      set(parsed);
                    } catch {
                      setCustomJsonError("Invalid JSON — not saved until this is fixed.");
                    }
                  }}
                />
              </Field>
              {customJsonError && (
                <div className="wf-panel-hint" style={{ color: "#cf1322" }}>{customJsonError}</div>
              )}
              <div className="wf-panel-hint">
                Extra parameters this custom action needs (e.g. <code>targetStatus</code>). Label and
                action type are managed above and don't need to be repeated here.
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

/* ── Editor ───────────────────────────────────────────────────────────── */
export default function WorkflowGraphEditor({
  value, onChange, fieldOptions = [],
  anchorModule, relatedModules = [], fieldsByModule = {}, modules = [], lookups = {},
}) {
  const authInfo = useSelector((s) => s.auth?.auth_info || {});
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [customActions, setCustomActions] = useState([]);
  const inited = useRef(false);
  const syncingFromValue = useRef(false);
  const reactFlowInstance = useRef(null);
  const onChangeRef = useRef(onChange);
  const lastEmittedRef = useRef(null);
  onChangeRef.current = onChange;

  useEffect(() => {
    let mounted = true;

    const loadAssignees = async () => {
      try {
        const [usersRes, rolesRes] = await Promise.all([
          getUserApi({ "select-type": "active" }).catch(() => null),
          authInfo?.id
            ? getUserRoleSubscriptionWiseListApi({ id: authInfo.subscription_id, institution_id: authInfo.id }).catch(() => null)
            : Promise.resolve(null),
        ]);

        if (!mounted) return;

        const users = usersRes?.data?.message || usersRes?.data || [];
        // Same source + merge as the create-user screen (UserManage.jsx's loadRoles):
        // default/subscription roles union'd with this institution's own custom roles.
        const subscriptionBaseData = rolesRes?.data?.subscriptionBaseData || [];
        const userAddedRoles = rolesRes?.data?.userAddedRoles || [];
        const roles = [...subscriptionBaseData, ...userAddedRoles];

        setUserOptions(
          (Array.isArray(users) ? users : []).map((u) => ({
            value: u.id,
            label: `${u.full_name || u.name || u.email || "User"}${u.email ? ` (${u.email})` : ""}`,
          }))
        );

        setRoleOptions(
          roles.map((r) => {
            const parts = String(r.role_name || "").split("_");
            const label = parts.length > 2 ? parts.slice(2).join(" ") : (r.role_name || r.id);
            return { value: r.id, label };
          })
        );
      } catch {
        if (!mounted) return;
        setUserOptions([]);
        setRoleOptions([]);
      }
    };

    loadAssignees();
    return () => { mounted = false; };
  }, [authInfo?.id]);

  // Custom action handlers registered under backend/src/workflow/custom_actions/
  // — fetched once so a new file there becomes selectable with no frontend changes.
  useEffect(() => {
    let mounted = true;
    listCustomActions()
      .then((res) => { if (mounted) setCustomActions(res?.data?.data || []); })
      .catch(() => { if (mounted) setCustomActions([]); });
    return () => { mounted = false; };
  }, []);

  const handleInit = useCallback((instance) => {
    reactFlowInstance.current = instance;
    requestAnimationFrame(() => instance.fitView({ padding: 0.16 }));
  }, []);

  // Initialise from the incoming graph whenever it changes.
  // Preserve ReactFlow's `selected` state so the selection panel doesn't
  // flicker off when the parent echoes back the graph after onChange.
  useEffect(() => {
    const g = value && Array.isArray(value.nodes) && value.nodes.length ? value : DEFAULT_GRAPH;
    syncingFromValue.current = true;
    setNodes((currentNodes) => {
      const selMap = new Map(currentNodes.map((n) => [n.id, n.selected ?? false]));
      return (g.nodes || []).map((n, i) => ({
        id: n.id,
        type: ["start", "condition", "action"].includes(n.type) ? n.type : "action",
        position: n.position || { x: 240, y: 30 + i * 110 },
        data: { ...(n.data || {}) },
        selected: selMap.get(n.id) ?? false,
      }));
    });
    setEdges((g.edges || []).map((e, i) => ({
      id: e.id || `e_${i}`,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      label: e.sourceHandle || undefined,
      markerEnd: { type: MarkerType.ArrowClosed },
    })));
    inited.current = true;
  }, [value, setNodes, setEdges]);

  // Emit the engine graph (strip reactflow-only fields) whenever it changes.
  // Only fire onChange when the serialisable content actually changed —
  // selection-state-only changes (from clicking a node) must NOT propagate
  // to the parent, otherwise the parent echoes the value back, the [value]
  // effect fires, setNodes resets ReactFlow's selection, and the panel
  // disappears before the user sees it.
  useEffect(() => {
    if (!inited.current) return;
    if (syncingFromValue.current) {
      syncingFromValue.current = false;
      return;
    }

    const next = {
      nodes: nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        ...(e.sourceHandle ? { sourceHandle: e.sourceHandle } : {}),
      })),
    };

    const serialised = JSON.stringify(next);
    if (serialised === lastEmittedRef.current) return;
    lastEmittedRef.current = serialised;

    onChangeRef.current?.(next);
  }, [nodes, edges]);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge({ ...params, label: params.sourceHandle || undefined, markerEnd: { type: MarkerType.ArrowClosed } }, eds)
      ),
    [setEdges]
  );

  const addNode = (type) => {
    const id = `${type}_${Date.now()}`;
    const data = type === "condition"
      ? { label: "Condition", conditions: { type: "group", joiner: "AND", conditions: [{ field: "", operator: "equals", valueType: "static", value: "" }] } }
      : { label: "Action", actionType: "Send Notification" };
    setNodes((nds) => nds.concat({ id, type, position: { x: 360, y: 60 + nds.length * 90 }, data }));
    setSelectedId(id);
  };

  const updateNodeData = (patch) =>
    setNodes((nds) => nds.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, ...patch } } : n)));

  const deleteSelected = () => {
    setNodes((nds) => nds.filter((n) => n.id !== selectedId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
  };

  const selectedNode = useMemo(() => nodes.find((n) => n.id === selectedId) || null, [nodes, selectedId]);

  return (
    <div className="wf-graph-editor">
      <div className="wf-graph-canvas">
        <div className="wf-toolbar">
          <Button size="small" icon={<PlusOutlined />} onClick={() => addNode("condition")}>Condition</Button>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => addNode("action")}>Action</Button>
        </div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onInit={handleInit}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={useCallback(
            ({ nodes: sel }) => setSelectedId(sel.length === 1 ? sel[0].id : null),
            []
          )}
          nodeTypes={nodeTypes}
          deleteKeyCode={["Backspace", "Delete"]}
          style={{ width: "100%", height: "100%" }}
        >
          <Background />
          <Controls />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
      <PropertyPanel
        node={selectedNode}
        onChange={updateNodeData}
        onDelete={deleteSelected}
        fieldOptions={fieldOptions}
        userOptions={userOptions}
        roleOptions={roleOptions}
        customActions={customActions}
        anchorModule={anchorModule}
        relatedModules={relatedModules}
        fieldsByModule={fieldsByModule}
        modules={modules}
        lookups={lookups}
      />
    </div>
  );
}
